import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent } from 'react';

import { ListIcon, XIcon } from '@phosphor-icons/react';
import { AnimatePresence, motion, type TargetAndTransition } from 'motion/react';
import { useLocation, useNavigate } from 'react-router-dom';

import { ROUTES } from '@shared/config/routes';
import { cn } from '@shared/lib/utils';
import { buttonVariants } from '@shared/ui/button';
import { CortexaLogo } from '@shared/ui/cortexa-logo';
import { ToggleGroup, ToggleGroupItem } from '@shared/ui/toggle-group';

import { LANDING_SCROLL_ROOT_ID } from '../model/scroll';

const transitionDuration = 0.3;

const contentAnimationTargets = {
  enter: {
    opacity: 1,
    transition: { duration: transitionDuration / 2, ease: 'easeOut' },
  },
  exit: {
    opacity: 0,
    transition: { duration: transitionDuration / 10, ease: 'easeIn' },
  },
} satisfies Record<'enter' | 'exit', TargetAndTransition>;

interface LandingHeaderItem<T extends string = string> {
  id: T;
  label: string;
}

interface LandingHeaderNavigationProps<T extends string = string> {
  activeSection: T | null;
  items: readonly LandingHeaderItem<T>[];
  onSectionClick: (sectionId: T) => void;
  title?: never;
}

interface LandingHeaderTitleProps {
  title: string;
  activeSection?: never;
  items?: never;
  onSectionClick?: never;
}

export type LandingHeaderProps<T extends string = string> =
  | LandingHeaderNavigationProps<T>
  | LandingHeaderTitleProps;

export const LandingHeader = <T extends string>({
  activeSection,
  items,
  onSectionClick,
  title,
}: LandingHeaderProps<T>) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const headerPanelRef = useRef<HTMLDivElement | null>(null);
  const collapsedPanelMeasureRef = useRef<HTMLDivElement | null>(null);
  const [isMobileMenuOpenState, setIsMobileMenuOpen] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(false);
  const [collapsedPanelWidth, setCollapsedPanelWidth] = useState<number | null>(null);
  const navItems = title === undefined ? items : [];
  const hasLandingNavigation = navItems.length > 0;
  const shouldAnimateMobileWidth = hasLandingNavigation && isMobileViewport;
  const isMobileMenuOpen = hasLandingNavigation && isMobileMenuOpenState;
  const mobileMenuId = 'landing-header-mobile-menu';
  const expandedMobileHeaderWidth = 'min(calc(100vw - 2rem), 16rem)';
  const collapsedMobileHeaderWidth =
    collapsedPanelWidth === null ? 'auto' : `${String(collapsedPanelWidth)}px`;
  const headerWidth = shouldAnimateMobileWidth
    ? isMobileMenuOpen
      ? expandedMobileHeaderWidth
      : collapsedMobileHeaderWidth
    : 'auto';

  const handleLogoClick = () => {
    if (pathname === ROUTES.HOME) {
      setIsMobileMenuOpen(false);
      document
        .getElementById(LANDING_SCROLL_ROOT_ID)
        ?.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      return;
    }

    navigate(ROUTES.HOME);
  };

  const handleMobileSectionClick = (event: MouseEvent<HTMLAnchorElement>, sectionId: T) => {
    event.preventDefault();
    setIsMobileMenuOpen(false);
    onSectionClick?.(sectionId);
  };

  useEffect(() => {
    if (!hasLandingNavigation) {
      setIsMobileMenuOpen(false);
    }
  }, [hasLandingNavigation]);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;

      if (target instanceof Node && headerPanelRef.current?.contains(target)) {
        return;
      }

      setIsMobileMenuOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!hasLandingNavigation) {
      setIsMobileViewport(false);
      return;
    }

    const breakpoint = window.matchMedia('(min-width: 40rem)');
    const handleBreakpointChange = () => {
      const isDesktopViewport = breakpoint.matches;

      setIsMobileViewport(!isDesktopViewport);

      if (isDesktopViewport) {
        setIsMobileMenuOpen(false);
      }
    };

    handleBreakpointChange();
    breakpoint.addEventListener('change', handleBreakpointChange);

    return () => {
      breakpoint.removeEventListener('change', handleBreakpointChange);
    };
  }, [hasLandingNavigation]);

  useLayoutEffect(() => {
    if (!hasLandingNavigation) {
      setCollapsedPanelWidth(null);
      return;
    }

    const measurePanel = collapsedPanelMeasureRef.current;

    if (!measurePanel) {
      return;
    }

    const updateCollapsedPanelWidth = () => {
      const nextWidth = measurePanel.getBoundingClientRect().width;

      if (nextWidth === 0) {
        return;
      }

      setCollapsedPanelWidth((currentWidth) => {
        if (currentWidth !== null && Math.abs(currentWidth - nextWidth) < 0.5) {
          return currentWidth;
        }

        return nextWidth;
      });
    };

    updateCollapsedPanelWidth();

    const resizeObserver = new ResizeObserver(updateCollapsedPanelWidth);
    resizeObserver.observe(measurePanel);
    window.addEventListener('resize', updateCollapsedPanelWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateCollapsedPanelWidth);
    };
  }, [hasLandingNavigation]);

  return (
    <motion.header
      layoutRoot
      className="fixed inset-x-0 top-(--landing-header-offset) z-50 flex justify-center px-4"
    >
      {hasLandingNavigation && (
        <div
          ref={collapsedPanelMeasureRef}
          aria-hidden="true"
          className="
            pointer-events-none absolute top-0 left-0 inline-flex h-(--landing-header-height)
            -translate-x-[200vw] items-center overflow-hidden rounded-(--landing-header-radius)
            bg-sidebar p-1 opacity-0
            sm:hidden
          "
        >
          <div
            className="
              relative flex h-10 items-center
              md:h-11
            "
          >
            <div
              className="
                relative me-3 rounded-full px-1.5 py-1
                sm:me-1
              "
            >
              <CortexaLogo
                size="md"
                icon={false}
                className="
                  relative h-6.5 w-auto
                  md:h-8
                  xl:h-9
                "
              />
            </div>
          </div>
          <div className="-ms-2 inline-flex size-10 items-center justify-center rounded-full">
            <ListIcon className="size-5" />
          </div>
        </div>
      )}
      <motion.div
        ref={headerPanelRef}
        initial={false}
        animate={{
          width: headerWidth,
          height: isMobileMenuOpen ? 'auto' : 'var(--landing-header-height)',
        }}
        className="
          relative inline-flex min-h-(--landing-header-height) flex-col overflow-hidden
          rounded-(--landing-header-radius) bg-sidebar p-1 shadow-lg ring-1 ring-border/70
          [corner-shape:round]
          sm:p-2
          md:p-2.5
          xl:p-3
        "
        transition={{
          width: {
            duration: transitionDuration * 2,
            ease: [0.22, 1, 0.36, 1],
          },
          height: {
            duration: transitionDuration * 2,
            ease: [0.22, 1, 0.36, 1],
          },
        }}
      >
        <div
          className={cn(
            `
              relative flex h-10 items-center
              md:h-11
            `,
            shouldAnimateMobileWidth && 'w-full justify-between'
          )}
        >
          <div
            className="
              relative flex h-10 items-center
              md:h-11
            "
          >
            <motion.button
              layout="x"
              key="logo"
              type="button"
              onClick={handleLogoClick}
              aria-label="Go to landing page"
              className="
                relative me-3 rounded-full px-1.5 py-1 outline-hidden transition-opacity
                duration-200 will-change-transform
                hover:opacity-85
                focus-visible:ring-2 focus-visible:ring-ring/60
                sm:me-4
              "
            >
              <CortexaLogo
                size="md"
                icon={false}
                className="
                  relative h-6.5 w-auto
                  md:h-8
                  xl:h-9
                "
              />
            </motion.button>
          </div>
          <div
            className="
              relative hidden h-10 overflow-hidden rounded-full
              sm:block
              md:h-11
            "
          >
            <AnimatePresence initial={false} mode="popLayout">
              {title !== undefined ? (
                <motion.div
                  layout="position"
                  layoutAnchor={{ x: 1, y: 0.5 }}
                  key="title"
                  initial={contentAnimationTargets.exit}
                  animate={contentAnimationTargets.enter}
                  exit={contentAnimationTargets.exit}
                  className="
                    me-4 flex h-10 items-center justify-center px-1 text-sm/5 font-medium
                    text-foreground-3
                    sm:me-5
                    md:h-11 md:text-base
                    lg:text-lg
                  "
                >
                  <span className="block size-fit">{title}</span>
                </motion.div>
              ) : (
                <motion.div
                  layout="position"
                  layoutAnchor={{ x: 1, y: 0.5 }}
                  key="nav"
                  initial={contentAnimationTargets.exit}
                  animate={contentAnimationTargets.enter}
                  exit={contentAnimationTargets.exit}
                  className="
                    h-10
                    md:h-11
                  "
                >
                  <ToggleGroup<T>
                    aria-label="Landing page sections"
                    value={activeSection ? [activeSection] : []}
                    className="gap-px rounded-full bg-background-2 p-1 inset-shadow-xs"
                    onValueChange={(groupValue) => {
                      const nextSection = groupValue[0];

                      if (nextSection) {
                        onSectionClick(nextSection);
                      }
                    }}
                  >
                    {navItems.map(({ id, label }) => (
                      <ToggleGroupItem<T>
                        key={id}
                        value={id}
                        aria-label={label}
                        activeClassName={cn(
                          buttonVariants({ variant: 'default', size: 'lg' }),
                          `
                            h-8 rounded-full
                            md:h-9
                          `
                        )}
                        inactiveClassName={cn(
                          buttonVariants({ variant: 'ghost', size: 'lg' }),
                          `
                            h-8 rounded-full
                            md:h-9
                          `
                        )}
                      >
                        {label}
                      </ToggleGroupItem>
                    ))}
                  </ToggleGroup>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          {hasLandingNavigation && (
            <motion.button
              type="button"
              aria-label={isMobileMenuOpen ? 'Close landing navigation' : 'Open landing navigation'}
              aria-controls={mobileMenuId}
              aria-expanded={isMobileMenuOpen}
              onClick={() => {
                setIsMobileMenuOpen((isOpen) => !isOpen);
              }}
              className="
                -ms-2 inline-flex size-10 items-center justify-center rounded-full text-foreground-3
                outline-hidden transition-colors will-change-transform
                hover:bg-background-2 hover:text-foreground
                focus-visible:ring-2 focus-visible:ring-ring/60
                sm:hidden
              "
            >
              {isMobileMenuOpen ? <XIcon className="size-5" /> : <ListIcon className="size-5" />}
            </motion.button>
          )}
        </div>
        <AnimatePresence initial={false}>
          {isMobileMenuOpen && (
            <motion.nav
              id={mobileMenuId}
              key="mobile-nav"
              aria-label="Landing page sections"
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{
                duration: transitionDuration,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="
                mt-1 flex flex-col gap-1 rounded-[calc(var(--landing-header-radius)-0.25rem)] border
                border-border p-1
                sm:hidden
              "
            >
              {navItems.map(({ id, label }) => (
                <a
                  key={id}
                  href={`#${id}`}
                  onClick={(event) => {
                    handleMobileSectionClick(event, id);
                  }}
                  className="
                    flex h-11 items-center rounded-full px-4 text-base/5 font-medium tracking-tight
                    text-foreground-2 outline-hidden transition-colors
                    hover:text-foreground
                    focus-visible:ring-2 focus-visible:ring-ring/60
                  "
                >
                  {label}
                </a>
              ))}
            </motion.nav>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.header>
  );
};
